/* istanbul ignore file: currently content service still returns fake data for some content types. Please remove this comment after all content types are using real data. */
import { ContentType } from '../models/ContentType';
import { SearchResultList, SearchResultItem, MixedContentTypeSearchResult } from '../models/SearchResultTypes';
import { FilterQuery } from '../state/FilterState';
import { CRASSService } from './CRASSService';
import { ContentDefinitions } from '../models/contentDefinitions';
import { WebClientResourceService } from './httpClient/WebClientResourceService';
import { IPagedItemsParams } from '../models/httpClient/IListFetcher';
import { SortSelection } from '../models/SortSelection';
import { IUserWithPublicInfo } from '../models/httpClient/MetaDataResults';
import GetMetaDataRequests from './httpClient/GetMetaDataRequests';
import { FavoriteService } from './FavoriteService';

export const CRASSResponseMaxCount = 10000;

export class ContentService {
  public static instance = new ContentService();

  public fetchAllContent(
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery
  ): Promise<MixedContentTypeSearchResult> {
    return new Promise<MixedContentTypeSearchResult>((resolve, reject) => {
      const mixedContentTypeResult: MixedContentTypeSearchResult = {
        contentTypeOrder: [],
        map: {} as { [type in ContentType]: SearchResultList<SearchResultItem> },
      };
      const promises: Promise<SearchResultList<SearchResultItem>>[] = [];
      for (const contentType in ContentDefinitions) {
        const currentContentType = contentType as ContentType;
        promises.push(ContentService.instance.fetchContent(currentContentType, searchTerm, startIndex, pageSize, filter));
      }

      Promise.all(promises).then((results) => {
        const resultLists: SearchResultList<SearchResultItem>[] = results;

        const list: { type: ContentType; score: number }[] = [];
        let errorCount = 0;
        const callCount = resultLists.length;

        mixedContentTypeResult.map = resultLists.reduce<{ [type in ContentType]: SearchResultList<SearchResultItem> }>((p, c) => {
          p[c.type] = c;
          errorCount += c.error ? 1 : 0;

          const score =
            c.list.reduce<number>((p, c) => {
              return p + (c.crassData?.score || 0);
            }, 0) / c.list.length;
          list.push({ type: c.type, score: score || 0 });

          return p;
        }, {} as { [type in ContentType]: SearchResultList<SearchResultItem> });
        if (callCount === errorCount) {
          mixedContentTypeResult.error = Object.values(mixedContentTypeResult.map)[0].error;
        }
        const sortedList = list.sort((a, b) => b.score - a.score);
        const contentTypeOrder = sortedList.map((item) => item.type);
        mixedContentTypeResult.contentTypeOrder = contentTypeOrder;

        resolve(mixedContentTypeResult);
      });
    });
  }

  public fetchContent(
    type: ContentType,
    searchTerm: string,
    startIndex: number,
    pageSize: number,
    filter?: FilterQuery,
    sortBy?: SortSelection
  ): Promise<SearchResultList<SearchResultItem>> {
    return new Promise<SearchResultList<SearchResultItem>>((resolve, reject) => {
      if (ContentDefinitions[type].shouldBeAvailable()) {
        CRASSService.instance.search(type, searchTerm, startIndex, pageSize, filter, sortBy).then((crassResponse) => {
          if (crassResponse.error) {
            resolve({
              type: type,
              list: [],
              totalCount: crassResponse.totalCount,
              error: crassResponse.error,
            });
          } else {
            const result: SearchResultList<SearchResultItem> = {
              type: type,
              list: [],
              totalCount: crassResponse.totalCount,
            };

            const mapCRASSData = ContentDefinitions[type].mapCRASSData;
            if (mapCRASSData) {
              result.list = crassResponse.list.length > 0 ? mapCRASSData(crassResponse.list, crassResponse.drafts) : [];
            }
            resolve(result);
          }
        });
      } else {
        resolve({
          type: type,
          list: [],
          totalCount: 0,
          error: ContentServiceError.SHOULD_NOT_BE_AVAILABLE,
        });
      }
    });
  }

  public fetchContentMetadata(
    type: ContentType,
    searchResultItems: SearchResultItem[]
  ): Promise<{ contentType: ContentType; data: SearchResultItem[] } | null> {
    return new Promise<{ contentType: ContentType; data: SearchResultItem[] } | null>((resolve, reject) => {
      if (searchResultItems.length && ContentDefinitions[type].shouldBeAvailable()) {
        const ids = searchResultItems.map((item) => {
          const customID = ContentDefinitions[type].getCustomID;
          return customID ? customID(item) : item.id.toString();
        });
        const getCustomMetaData = ContentDefinitions[type].getCustomMetaDataRequests;
        if (getCustomMetaData) {
          getCustomMetaData(ids).then((response) => {
            const mapMetadata = ContentDefinitions[type].mapMetadata;
            let result: SearchResultItem[] = [];
            if (mapMetadata) {
              result = mapMetadata(searchResultItems, response);
            }
            resolve({ contentType: type, data: result });
          });
        } else if (ContentDefinitions[type].getMetaDataRequests) {
          new WebClientResourceService(ContentDefinitions[type].getMetaDataRequests)
            .fetchList({
              filter: {
                operator: 'and',
                clauses: [{ operator: 'in', field: 'id', values: ids }],
              },
            } as IPagedItemsParams)
            .then((response) => {
              const mapMetadata = ContentDefinitions[type].mapMetadata;
              let result: SearchResultItem[] = [];
              if (mapMetadata) {
                result = mapMetadata(searchResultItems, response);
              }
              resolve({ contentType: type, data: result });
            })
            .catch((error) => {
              resolve(null);
            });
        }
      } else {
        resolve(null);
      }
    });
  }

  public getMissingUser(userId: string): Promise<{ result: IUserWithPublicInfo } | null> {
    return new Promise<{ result: IUserWithPublicInfo } | null>((resolve) => {
      new WebClientResourceService(GetMetaDataRequests.GetUsersRequest)
        .fetchList({
          filter: {
            operator: 'and',
            clauses: [{ operator: 'in', field: 'id', values: [userId] }],
          },
        } as IPagedItemsParams)
        .then((response) => {
          if (response.total) {
            resolve({ result: response.items.users[0] });
          } else {
            resolve(null);
          }
        })
        .catch(() => {
          resolve(null);
        });
    });
  }

  public addFavorite(item: SearchResultItem): Promise<void> {
    return FavoriteService.instance.addFavorite(item);
  }

  public removeFavorite(item: SearchResultItem): Promise<void> {
    return FavoriteService.instance.removeFavorite(item);
  }
}

export enum ContentServiceError {
  SHOULD_NOT_BE_AVAILABLE = 'CONTENT_TYPE/SHOULD_NOT_BE_AVAILABLE',
}
